"use client";

/* eslint-disable react-hooks/set-state-in-effect -- honest history fetch on mount is intentional (SSR fallback) */

// 2026-09-03 status — honest 90-day history (replaces synthetic bars)
// Free-tier safe: 1 row/day via /api/cron/health, real snapshots drive bars + uptime
import { useCallback, useEffect, useRef, useState } from "react";
import Link from "next/link";
import { Check, CheckCircle2, ChevronDown, AlertTriangle, XCircle, Clock3, Mail, RefreshCw, Loader2 } from "lucide-react";
import { applyInfraPhase, componentStateLabelKey, mapHealthToStatusPage, overallStateLabelKey, type HealthPayload, type InfraStatusPayload, type OverallState, type StatusPageModel } from "@/lib/status-page";
import { useT, useIntlLocale, useLocale } from "@/lib/use-i18n";
import type { TranslationKey } from "@/lib/i18n";
import { localePath, type Locale } from "@/lib/i18n/locales";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LogoMark } from "@/components/ui/logo";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import type { StatusHistory } from "@/lib/status-history";
const POLL_MS = 180_000;
// 2026-09-24 FASE 4 MEJORA transversal M4 (research 2026 MDN
// AbortSignal.timeout 2026-04-13 + jamdesk AbortController guide
// 2026-04-04, paridad OverloadBanner fetchTimeoutMs): el poll caliente
// (180s, respeta max-age=60) nunca debe colgar — un edge colgado apilaba
// fetches entre ticks; el timeout lo corta y el catch silencioso mantiene
// el modelo previo hasta el reintento. Sin cambio visual.
const STATUS_FETCH_TIMEOUT_MS = 10_000;
function statusFetchSignal(): AbortSignal | undefined {
  try {
    return typeof AbortSignal.timeout === "function" ? AbortSignal.timeout(STATUS_FETCH_TIMEOUT_MS) : undefined;
  } catch {
    return undefined;
  }
}
// 2026-09-14 L-072: HISTORY_POLL_MS eliminado — el history (≈600s, cambia
// 1x/día) lo pide el mismo tick 180s cada 4 ciclos (≈12min ≥ techo 600s).

// 2026-09-28 SEED 2026-09-28-bae4824d #26 (Free-tier CPU budget: one owner per
// poll + module-level dedup ≥30s con shared in-flight, paridad
// fetchUsageShared/fetchInfraStatusShared): fetchHistory iba directo a red —
// mount sin SSR + tick coincidente = 2 SELECT seguidos al mismo endpoint.
// Slot dedicado por tab (ventana 30s + in-flight): colapsa solapes reales sin
// suprimir re-checks secuenciales. Endpoint distinto al de infra-status: slot
// propio (compartir aquel bloquearía history tras un poll de infra).
let historyInflight: Promise<void> | null = null;
let historyLastMs = 0;
const HISTORY_DEDUP_MS = 30_000;

/** Test-only: resetea el slot de history entre tests. */
export function __resetStatusHistoryDedupForTests(): void {
  historyInflight = null;
  historyLastMs = 0;
}
interface StatusRowDef {
  id: string;
  componentsCount: number;
  sub: TranslationKey[];
}

// 2026-09-21 FASE 4 i18n P1 (Google Search Central 2026 localized-versions —
// hreflang solo con traducción real + AGENTS.md § i18n 100% sin fallback):
// los subs iban en español fijo (13 locales veían "Conversaciones…"). Ahora
// son keys statusSub* con nativa en las 14 (audit:i18n + translation-coverage
// en verde) y se renderizan vía t(), igual que nameKey/state.
const ROW_DEFS: Record<string, StatusRowDef> = {
  chat: {
    id: "chat",
    componentsCount: 4,
    sub: ["statusSubConversations", "statusSubFiles", "statusSubCanvas", "statusSubExport"]
  },
  api: {
    id: "api",
    componentsCount: 3,
    sub: ["statusSubChat", "statusSubPublicApi", "statusSubState"]
  },
  images: {
    id: "images",
    componentsCount: 2,
    sub: ["statusSubGeneration", "statusSubLibrary"]
  },
  research: {
    id: "research",
    componentsCount: 2,
    sub: ["statusSubSearch", "statusSubSynthesis"]
  },
  study: {
    id: "study",
    componentsCount: 3,
    sub: ["statusSubProjects", "statusSubTasks", "statusSubExam"]
  },
  web: {
    id: "web",
    componentsCount: 2,
    sub: ["statusSubWeb", "statusSubPanel"]
  },
  database: {
    id: "database",
    componentsCount: 2,
    sub: ["statusSubData", "statusSubAccounts"]
  },
  payments: {
    id: "payments",
    componentsCount: 2,
    sub: ["statusSubPayments", "statusSubSubscription"]
  }
};
const DISPLAY_ORDER = ["chat", "api", "images", "research", "study", "web", "database", "payments"] as const;

// 2026-09-05 tokens: semáforos con vars (respetan light/dark sin hex duplicados)
function barColor(state: "operational" | "degraded" | "outage" | "unknown") {
  if (state === "outage") return "bg-[var(--danger)]";
  if (state === "degraded") return "bg-[var(--warning)]";
  if (state === "unknown") return "bg-muted-foreground/30";
  return "bg-[var(--success)]";
}

// 2026-09-23 FASE 3 P1 contraste (research 2026 WebAIM Million + W3C 1.4.3/1.4.11:
// blanco sobre --success dark #22d45e ≈1.7:1, falla texto 4.5:1 y non-text 3:1;
// --badge-*-foreground SSOT F7 da tinta oscura en dark (9.6/8.8/4.8) y blanco en
// light ≥5.0 — light intacto, dark AA sin superficie nueva).
function overallBannerClasses(overall: OverallState) {
  if (overall === "operational") return "bg-[var(--success)]/10 border-[var(--success)]/25 text-[var(--success-text)]";
  if (overall === "degraded") return "bg-[var(--warning)]/10 border-[var(--warning)]/25 text-[var(--warning)]";
  return "bg-[var(--danger)]/10 border-[var(--danger)]/25 text-[var(--danger)]";
}
function overallIcon(overall: OverallState) {
  if (overall === "operational") return <Check className="size-4 text-[var(--badge-success-foreground)]" strokeWidth={3} />;
  if (overall === "degraded") return <AlertTriangle className="size-4 text-[var(--badge-warning-foreground)]" />;
  return <XCircle className="size-4 text-[var(--badge-danger-foreground)]" />;
}
function overallCircleBg(overall: OverallState) {
  if (overall === "operational") return "bg-[var(--success)]";
  if (overall === "degraded") return "bg-[var(--warning)]";
  return "bg-[var(--danger)]";
}
interface StatusDashboardProps {
  initialModel: StatusPageModel;
  initialHistory?: StatusHistory | null;
}
export function StatusDashboard({
  initialModel,
  initialHistory = null
}: StatusDashboardProps) {
  const t = useT();
  const intlLocale = useIntlLocale();
  // useLocale may be mocked incompletely in tests — fallback to 'en' if unavailable
  let locale: Locale = "en";
  try {
    const maybe = (useLocale as unknown as () => string | undefined)?.();
    if (typeof maybe === "string" && maybe.length > 0) locale = maybe as Locale;
  } catch {
    locale = "en";
  }
  // 2026-09-02 hydration fix — Seer JAVASCRIPT-NEXTJS-1: server renders without locale vs client /es/status
  // Date strings are locale/timezone dependent (Vercel UTC vs client America/Bogota) -> mismatch.
  // Render ISO initially, then hydrate to localized after mount + suppressHydrationWarning on root.
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => setIsMounted(true), []);
  const [model, setModel] = useState(initialModel);
  const [history, setHistory] = useState<StatusHistory | null>(initialHistory);
  const [refreshing, setRefreshing] = useState(false);
  const [email, setEmail] = useState("");
  // 2026-09-21 FASE 4 a11y P3 (WCAG 2.2 3.3.1 + 4.1.3, paridad
  // SubscriptionCardForm/ContactForm/SuggestionForm): el email inválido antes
  // solo toast (efímero, sin aria-invalid). Ahora error inline con id estable
  // + role=alert; el toast de red se conserva (el diálogo sigue abierto).
  const [subscribeError, setSubscribeError] = useState<string | null>(null);
  const [subLoading, setSubLoading] = useState(false);
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  // 2026-09-24 FASE 4 transversal M5 (paridad NewsletterSignup P2 a11y:
  // mover foco al campo inválido para lectores de pantalla — research 2026
  // W3C WCAG 2.2 3.3.1 + veeform/testparty: el error inline con role=alert
  // existe, pero sin foco el SR no lo anunciaba en el diálogo).
  const subscribeInputRef = useRef<HTMLInputElement>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const fetchHistory = useCallback(async (force = false) => {
    // 2026-09-07 CPU budget: no pollear en tab oculta/offline (igual que refresh).
    if (typeof document !== "undefined" && document.hidden) return;
    if (typeof navigator !== "undefined" && !navigator.onLine) return;
    // 2026-09-28 SEED 2026-09-28-bae4824d #26: slot 30s/in-flight — mount +
    // tick coincidente colapsan en 1 hit; force (no usado hoy, reservado para
    // refresh manual futuro) bypassea la ventana pero comparte el in-flight.
    if (historyInflight) return historyInflight;
    if (!force && Date.now() - historyLastMs < HISTORY_DEDUP_MS) return;
    if (!force) historyLastMs = Date.now();
    historyInflight = (async () => {
      try {
        // History cambia 1x/día (snapshot diario): respeta max-age=60 del server.
        const res = await fetch("/api/status/history", {
          headers: {
            Accept: "application/json"
          },
          signal: statusFetchSignal()
        });
        if (!res.ok) return;
        const json = (await res.json()) as {
          days?: StatusHistory["days"];
          bars?: StatusHistory["bars"];
          uptime?: string | null;
          snapshotCount?: number;
          range?: StatusHistory["range"];
          withDataCount?: number;
        };
        // Normalize to StatusHistory shape
        if (json && Array.isArray(json.bars) && Array.isArray(json.days)) {
          setHistory({
            days: json.days as StatusHistory["days"],
            bars: json.bars as StatusHistory["bars"],
            uptime: json.uptime as string | null ?? null,
            snapshotCount: json.snapshotCount as number ?? 0,
            withDataCount: json.withDataCount as number ?? 0,
            range: json.range as StatusHistory["range"] ?? null
          });
        }
      } catch {
        // fail-open: keep previous history
      } finally {
        historyInflight = null;
      }
    })();
    return historyInflight;
  }, []);
  const refresh = useCallback(async (manual = false) => {
    // Lote Fase 5 polling: no pollear en tab oculta/offline (CPU budget).
    if (typeof document !== "undefined" && document.hidden) return;
    if (typeof navigator !== "undefined" && !navigator.onLine) return;
    setRefreshing(true);
    try {
      if (manual) {
        // 2026-09-18 FASE 2: /api/health es diagnóstico manual (checks
        // completos, no-store) — solo refresh explícito del usuario, nunca
        // poll de intervalo.
        const res_0 = await fetch("/api/health", {
          headers: {
            Accept: "application/json"
          },
          signal: statusFetchSignal()
        });
        if (!res_0.ok) {
          setModel(mapHealthToStatusPage(null, {
            fetchFailed: true
          }));
        } else {
          const json_0 = (await res_0.json()) as HealthPayload;
          setModel(mapHealthToStatusPage(json_0));
        }
      } else {
        // 2026-09-18 FASE 2 perf (free-tier CPU budget): el poll de intervalo
        // usa /api/infra-status (snapshot en memoria, max-age=60 — respeta el
        // cache del server, sin cache:no-store en cliente) en vez del health
        // completo. Solo mueve overall + checkedAt (applyInfraPhase); el
        // detalle por componente viene del SSR inicial.
        // 2026-09-24 FASE 2 FIXER L-30 (slot compartido infra-status, paridad
        // OverloadBanner): el tick reclama la ventana 30s + in-flight por tab
        // — si el banner pidió hace <30s, este tick salta sin red (≤1 hit/30s).
        const {
          fetchInfraStatusShared
        } = await import("@/lib/proxy/infra-status-shared");
        const json_1 = await fetchInfraStatusShared({
          headers: {
            Accept: "application/json"
          },
          signal: statusFetchSignal()
        });
        if (!json_1) {
          return;
        }
        setModel(prev => applyInfraPhase(prev, json_1 as InfraStatusPayload));
      }
      // History no se re-pide en cada tick: cambia 1x/día, solo refresh manual.
    } catch {
      setModel(mapHealthToStatusPage(null, {
        fetchFailed: true
      }));
    } finally {
      setRefreshing(false);
    }
  }, []);
  useEffect(() => {
    // Initial history fetch if not provided via SSR (covers local dev without snapshot)
    if (!initialHistory) void fetchHistory();
  }, [initialHistory, fetchHistory]);
  useEffect(() => {
    // 2026-09-18 perf: start/stop por visibilitychange para no despertar tabs ocultas (free-tier CPU budget)
    let ticks = 0;
    let id = 0;
    const tick = () => {
      ticks += 1;
      // 2026-09-23 FASE 2 FIXER P3 (API fan-out ≤2 roundtrips, paridad
      // /api/usage): en el tick de historia los 2 reads van en UN solo
      // Promise.allSettled (paralelo, un settlement) en vez de 2 voids
      // sueltos; el resto de ticks solo pide infra-status. Los guards de
      // oculta/offline viven en refresh/fetchHistory.
      if (ticks % 4 === 0) {
        if (typeof document === "undefined" || document.hidden) return;
        if (typeof navigator !== "undefined" && !navigator.onLine) return;
        void Promise.allSettled([refresh(), fetchHistory()]);
        return;
      }
      void refresh();
    };
    const start = () => {
      if (id) return;
      id = window.setInterval(tick, POLL_MS);
    };
    const stop = () => {
      if (id) {
        window.clearInterval(id);
        id = 0;
      }
    };
    const onVisible = () => {
      if (typeof document === "undefined") return;
      if (document.hidden) {
        stop();
      } else {
        start();
        if (typeof navigator !== "undefined" && navigator.onLine) {
          void refresh();
        }
      }
    };
    if (typeof document !== "undefined" && !document.hidden) {
      start();
    }
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refresh, fetchHistory]);
  const checkedLabel = (() => {
    // Fix hydration: render placeholder before mount, then localized UTC after
    if (!isMounted) return model.checkedAt.slice(0, 16).replace("T", " ");
    try {
      return new Date(model.checkedAt).toLocaleString(intlLocale, {
        dateStyle: "medium",
        timeStyle: "medium",
        timeZone: "UTC"
      });
    } catch {
      return model.checkedAt;
    }
  })();
  const historyRangeLabel = (() => {
    if (history?.range?.from && history?.range?.to) {
      // Fix hydration: before mount use raw range to avoid locale/timezone mismatch
      if (!isMounted) return `${history.range.from.slice(0, 10)} – ${history.range.to.slice(0, 10)}`;
      try {
        const from = new Date(history.range.from).toLocaleDateString(intlLocale, {
          month: "short",
          day: "numeric",
          timeZone: "UTC"
        });
        const to = new Date(history.range.to).toLocaleDateString(intlLocale, {
          month: "short",
          day: "numeric",
          year: "numeric",
          timeZone: "UTC"
        });
        return `${from} – ${to}`;
      } catch {
        return `${history.range.from} – ${history.range.to}`;
      }
    }
    return t("statusSystemStatusRange");
  })();
  const hasRealHistory = Boolean(history && history.bars.length === 90 && history.withDataCount > 0);
  const overallUptime = hasRealHistory && history?.uptime ? history.uptime : null;
  // 2026-09-27 honest-status: ONE platform-level 90-day strip. Snapshots store
  // a single global status per day — rendering the same series under every row
  // implied per-component history that does not exist. Per-row current state
  // keeps its own probe (see status-page.ts); history is shown once, here.
  type BarState = "operational" | "degraded" | "outage" | "unknown";
  const platformBars: BarState[] = hasRealHistory ? history!.bars as BarState[] : Array(90).fill("unknown") as BarState[];
  const platformStripTitle = hasRealHistory ? history!.uptime ? `${history!.uptime} ${t("statusUptimeDays90")}` : t("statusHistoryCollecting") : t("statusHistoryCollecting");
  const onSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubscribeError(null);
    const trimmed = email.trim().toLowerCase();
    // 2026-09-24 FASE 4 transversal M2 (paridad NewsletterSignup /
    // ContactForm / SuggestionForm): el `.includes("@")` aceptaba
    // "a@b" sin TLD y el servidor (Zod .email()) lo rechazaba tras el
    // roundtrip. Mismo regex local, misma key 14, sin cambio visual.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      // Inline (no toast): el error vive junto al campo con role=alert —
      // el SR lo anuncia sin que el usuario pierda el foco del diálogo.
      // Reusa newsletterInvalidEmail (existe en las 14, sin keys nuevas).
      // M5: foco al campo como NewsletterSignup (rAF tras setState).
      setSubscribeError(t("newsletterInvalidEmail"));
      requestAnimationFrame(() => subscribeInputRef.current?.focus());
      return;
    }
    setSubLoading(true);
    try {
      const res_1 = await fetch("/api/newsletter", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify({
          email: trimmed
        })
      });
      const data = (await res_1.json().catch(() => ({}))) as {
        alreadySubscribed?: boolean;
        error?: string;
      };
      if (!res_1.ok) throw new Error(data.error || "fail");
      if (data.alreadySubscribed) toast.message(t("newsletterAlready"));else toast.success(t("newsletterSuccess"));
      setEmail("");
      setSubscribeError(null);
      setSubscribeOpen(false);
    } catch {
      // Inline (no toast): el diálogo sigue abierto — el error junto al campo
      // conserva el contexto; el toast duplicaría el anuncio del live region.
      setSubscribeError(t("newsletterError"));
    } finally {
      setSubLoading(false);
    }
  };
  const toggle = (id_0: string) => setExpanded(prev_0 => {
    const n = new Set(prev_0);
    if (n.has(id_0)) n.delete(id_0);else n.add(id_0);
    return n;
  });
  const ordered = DISPLAY_ORDER.map(id_1 => model.components.find(c => c.id === id_1)).filter(Boolean) as typeof model.components;
  const rows = ordered.length ? ordered : model.components;

  // 2026-09-24 FASE 3 FIXER dashboard (research 2026 NN/g progressive-disclosure +
  // Minimalista Pro ≤6 visibles, patrón AdminTabNav MAX_VISIBLE=5+···=6): 8 filas
  // a 360px saturan la vista — 5 visibles + resto en disclosure ··· (1 trigger =
  // 6 visibles máx). Orden estable, sin swap del activo.
  const MAX_VISIBLE = 5;
  const visibleRows = rows.slice(0, MAX_VISIBLE);
  const overflowRows = rows.slice(MAX_VISIBLE);

  // 2026-09-24 FASE 3 render fila extraído para reuso visible + overflow
  // (mismo markup/testids, sin duplicar lógica).
  // 2026-09-27 honest-status: sin tira de historial por fila (ver platformBars
  // en el encabezado de la sección — el historial es global, no por componente).
  const renderStatusRow = (comp: (typeof rows)[number]) => {
    const def = ROW_DEFS[comp.id] ?? {
      id: comp.id,
      componentsCount: 1,
      sub: []
    };
    const isExpanded = expanded.has(comp.id);
    const stateOk = comp.state === "operational";
    const stateDeg = comp.state === "degraded";
    // 2026-09-25 FASE 4 transversal oferta 9 i18n (research 2026 W3C WCAG
    // 2.2 Editor's Draft 2026-08-17 § title-tooltips + Google Search Central
    // 2026 localized-versions: los tooltips nativos servían literales EN de
    // estado y uptime + fallback ES bajo URLs /es|/fr…/status — ahora estado
    // vía t(componentStateLabelKey) y uptime vía statusUptimeDays90 (existe
    // en 14, 0 keys nuevas). Sin cambio visual.
    return <div key={comp.id} className="px-4 sm:px-5 py-4 hover:bg-muted transition-colors" role="listitem" data-testid={`status-component-${comp.id}`} data-state={comp.state}>
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
          <div className="flex items-center gap-2.5 min-w-0 flex-1">
            <span className={cn("inline-flex size-6 shrink-0 items-center justify-center rounded-full shadow-sm", stateOk && "bg-[var(--success)]", stateDeg && "bg-[var(--warning)]", comp.state === "outage" && "bg-[var(--danger)]", comp.state === "unknown" && "border border-border bg-muted")} aria-hidden="true">
              {stateOk ? <Check className="size-3.5 text-[var(--badge-success-foreground)]" strokeWidth={3} /> : stateDeg ? <AlertTriangle className="size-3 text-[var(--badge-warning-foreground)]" /> : comp.state === "outage" ? <XCircle className="size-3 text-[var(--badge-danger-foreground)]" /> : <Clock3 className="size-3 text-muted-foreground" />}
            </span>
            <span className="text-sm font-medium tracking-tight text-foreground truncate">
              {t(comp.nameKey as unknown as TranslationKey) || comp.id}
            </span>
            <button type="button" onClick={() => toggle(comp.id)} className="inline-flex min-h-7 shrink-0 whitespace-nowrap items-center gap-1 rounded-full bg-muted border border-border px-2.5 py-1 text-[11px] font-medium text-foreground-soft hover:bg-[var(--card)] hover:border-border-strong transition-colors" aria-expanded={isExpanded} aria-controls={`sub-${comp.id}`}>
              {t("statusComponentsCount", {
              count: String(def.componentsCount)
            })}
              {/* 2026-09-26 FASE 4 MEJORA transversal RONDA EXTRA M1 (research
                  2026 W3C WAI decorative-images + unimelb Accessible SVGs +
                  pingcap ossinsight #2608 2026-03-29: el botón ya tiene texto
                  ("N componentes") — el chevron es decorativo y sin
                  aria-hidden el SR lo anuncia como ruido. Paridad
                  Check/AlertTriangle/XCircle/Clock3 de la fila (ya ocultos).
                  Sin cambio visual, sin copy.) */}
              <ChevronDown aria-hidden="true" className={cn("size-3 transition-transform", isExpanded && "rotate-180")} />
            </button>
          </div>
          <div className="flex items-center gap-2.5 shrink-0">
            <span className="hidden sm:inline-flex items-center gap-1.5 text-[11px] text-muted-foreground whitespace-nowrap">
              <span className={cn("size-2 rounded-full shrink-0", stateOk ? "bg-[var(--success)]" : stateDeg ? "bg-[var(--warning)]" : comp.state === "outage" ? "bg-[var(--danger)]" : "bg-muted-foreground")} />
              {t(componentStateLabelKey(comp.state))}
            </span>
            <span className={cn("sm:hidden size-2 rounded-full shrink-0", stateOk ? "bg-[var(--success)]" : stateDeg ? "bg-[var(--warning)]" : comp.state === "outage" ? "bg-[var(--danger)]" : "bg-muted-foreground")} aria-hidden="true" />
          </div>
        </div>
        {isExpanded && <div id={`sub-${comp.id}`} className="mt-3 rounded-[var(--radius-panel)] border border-border bg-muted p-3.5">
            <ul className="space-y-2">
              {def.sub.map(s => <li key={s} className="flex items-center gap-2.5 text-xs text-foreground-soft">
                  {/* 2026-09-26 FASE 4 MEJORA transversal RONDA EXTRA M1 (misma
                    nota que el ChevronDown del toggle: el li ya tiene nombre
                    + estado en texto — el icono es decorativo). */}
                  <CheckCircle2 aria-hidden="true" className={cn("size-3.5 shrink-0", stateOk ? "text-[var(--success-text)]" : stateDeg ? "text-[var(--warning)]" : "text-muted-foreground")} />
                  <span className="flex-1">{t(s)}</span>
                  <span className="ml-auto text-[11px] font-medium text-muted-foreground whitespace-nowrap">
                    {t(componentStateLabelKey(comp.state))}
                  </span>
                </li>)}
            </ul>
          </div>}
      </div>;
  };
  return <div className="mx-auto w-full max-w-[760px] px-4 sm:px-0" data-testid="status-dashboard" suppressHydrationWarning>
      {/* Header — clean, high contrast — official Vortixy mark */}
      <header className="flex h-[56px] items-center justify-between border-b border-border bg-[var(--card)] -mx-4 px-4 sm:mx-0 sm:px-0 sm:bg-transparent sm:border-0">
        <Link href={localePath(locale, "/")} className="flex items-center gap-2.5 text-[15px] font-semibold tracking-tight text-foreground">
          <LogoMark size={28} className="rounded-lg shadow-sm" />
          Vortixy
        </Link>
        <button type="button" onClick={() => setSubscribeOpen(true)} className="inline-flex h-8 items-center rounded-full border border-border bg-[var(--card)] px-3.5 text-xs font-medium text-foreground-soft hover:bg-muted transition-colors shadow-sm">
          {t("statusSubscribeUpdates")}
        </button>
      </header>

      {/* Operational banner — fixed dark contrast, premium soft shadow */}
      <div className={cn("lovable-card hardware-surface rounded-[var(--radius-panel)] border border-border/80 shadow-xs mt-6 flex items-start gap-3 px-4 py-4 sm:px-5 sm:py-4", overallBannerClasses(model.overall))} data-testid="status-overall" data-state={model.overall} role="status" aria-live="polite">
        <span className={cn("mt-0.5 inline-flex size-7 shrink-0 items-center justify-center rounded-full shadow-sm", overallCircleBg(model.overall))} aria-hidden="true">
          {overallIcon(model.overall)}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-semibold leading-5 tracking-tight">
            {t(overallStateLabelKey(model.overall))}
          </p>
          <p className="mt-1 text-[13px] leading-5 text-foreground-soft">
            {model.overall === "operational" ? t("statusBannerSubtitle") : t("statusBannerSubtitleDegraded")}
          </p>
          {overallUptime && <p className="mt-1.5 text-[11px] font-medium text-foreground-soft">
              {overallUptime} {t("statusUptimeDays90")}
            </p>}
        </div>
        <Button type="button" variant="outline" size="sm" onClick={() => void refresh(true)} disabled={refreshing} className="hidden sm:inline-flex h-8 shrink-0 gap-1.5 rounded-full border-border bg-[var(--card)] px-3 text-xs font-medium hover:bg-muted shadow-sm" data-testid="status-refresh" aria-label={t("statusRefresh")}>
          <RefreshCw className={cn("size-3.5", refreshing && "animate-spin")} />
          <span>{t("statusRefresh")}</span>
        </Button>
      </div>

      {/* Mobile meta — better contrast */}
      <div className="mt-3 flex items-center justify-between gap-3 sm:hidden">
        <span className="text-[11px] leading-relaxed text-muted-foreground">
          {t("statusLastChecked")}:{" "}
          <span suppressHydrationWarning className="font-medium text-foreground-soft">
            {checkedLabel}
          </span>
          {typeof model.apiLatencyMs === "number" ? <span className="tabular-nums"> · {model.apiLatencyMs} ms</span> : null}
        </span>
        <Button type="button" variant="outline" size="sm" onClick={() => void refresh(true)} disabled={refreshing} className="h-8 shrink-0 gap-1.5 rounded-full px-3 text-xs border-border" data-testid="status-refresh-mobile">
          <RefreshCw className={cn("size-3.5", refreshing && "animate-spin")} />
          {t("statusRefresh")}
        </Button>
      </div>

      {/* System status — SINGLE COLUMN list (fixes 3-col grid overlap) */}
      <section className="mt-8 lovable-card hardware-surface rounded-[var(--radius-panel)] border border-border/80 shadow-xs overflow-hidden" aria-labelledby="status-system-heading">
        <div className="border-b border-border bg-muted px-4 sm:px-5 py-4">
          <div className="flex items-baseline justify-between gap-3">
            <h2 id="status-system-heading" className="text-sm font-semibold tracking-tight text-foreground">
              {t("statusSystemStatusHeading")}
            </h2>
            <span className="text-xs font-medium tabular-nums text-muted-foreground">
              {historyRangeLabel}
            </span>
          </div>
          <div className="mt-1.5 hidden sm:flex items-center gap-2 text-[11px] text-muted-foreground">
            <span>{t("statusLastChecked")}: </span>
            <span suppressHydrationWarning className="font-medium text-foreground-soft">
              {checkedLabel}
            </span>
            {typeof model.apiLatencyMs === "number" && <span className="tabular-nums">· {model.apiLatencyMs} ms</span>}
          </div>
          {!hasRealHistory && <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">
              {t("statusHistoryCollecting")}
            </p>}
        </div>

        {/* 2026-09-27 honest-status: historial ÚNICO a nivel plataforma (los
            snapshots guardan 1 estado global/día — no existe historial por
            componente). 100% real: gris = día sin snapshot aún. Altura
            uniforme h-16px (el color es el estado, sin varianza inventada);
            scroll-x honesto + resumen SR fuera del aria-hidden. */}
        <div className="border-b border-border px-4 sm:px-5 py-4" data-testid="status-history" data-history={hasRealHistory ? "real" : "empty"}>
          <p className="sr-only">{platformStripTitle}</p>
          <div className="overflow-x-auto rounded-sm [mask-image:linear-gradient(to_right,black_calc(100%-28px),transparent)]" data-testid="status-history-bars" title={platformStripTitle}>
            <div className="flex min-w-[320px] items-end gap-[2px] sm:gap-[3px]" aria-hidden="true">
              {platformBars.map((b, i) => {
              const dayDate = hasRealHistory && history?.days?.[i]?.date ? history.days[i]!.date : null;
              return <div key={dayDate ?? `bar-${i}`} className={cn("h-[16px] flex-1 min-w-[2px] max-w-[6px] rounded-sm", barColor(b))} style={{
                opacity: b === "operational" ? 0.92 : b === "unknown" ? 0.55 : 1
              }} title={dayDate ? `${dayDate} · ${t(componentStateLabelKey(b))}` : t(componentStateLabelKey(b))} />;
            })}
            </div>
          </div>
        </div>

        <div className="divide-y divide-border" role="list" data-testid="status-components">
          {visibleRows.map(comp_0 => {
          const def_0 = ROW_DEFS[comp_0.id] ?? {
            id: comp_0.id,
            componentsCount: 1,
            sub: []
          };
          const isExpanded_0 = expanded.has(comp_0.id);
          const stateOk_0 = comp_0.state === "operational";
          const stateDeg_0 = comp_0.state === "degraded";
          // 2026-09-27 honest-status: sin tira por fila (historial global en
          // el encabezado — platformBars). Sin datos no se inventa nada.
          return <div key={comp_0.id} className="px-4 sm:px-5 py-4 hover:bg-muted transition-colors" role="listitem" data-testid={`status-component-${comp_0.id}`} data-state={comp_0.state}>
                <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
                  {/* Left: icon + name + pill */}
                  <div className="flex items-center gap-2.5 min-w-0 flex-1">
                    <span className={cn("inline-flex size-6 shrink-0 items-center justify-center rounded-full shadow-sm", stateOk_0 && "bg-[var(--success)]", stateDeg_0 && "bg-[var(--warning)]", comp_0.state === "outage" && "bg-[var(--danger)]", comp_0.state === "unknown" && "border border-border bg-muted")} aria-hidden="true">
                      {stateOk_0 ? <Check className="size-3.5 text-[var(--badge-success-foreground)]" strokeWidth={3} /> : stateDeg_0 ? <AlertTriangle className="size-3 text-[var(--badge-warning-foreground)]" /> : comp_0.state === "outage" ? <XCircle className="size-3 text-[var(--badge-danger-foreground)]" /> : <Clock3 className="size-3 text-muted-foreground" />}
                    </span>
                    <span className="text-sm font-medium tracking-tight text-foreground truncate">
                      {t(comp_0.nameKey as unknown as TranslationKey) || comp_0.id}
                    </span>
                    {/* 2026-09-23 FASE 3 P3 target 28px (research 2026 WCAG 2.5.8:
                        el pill py-1 ≈26px — min-h-7 lo lleva a 28px sin romper
                        el wrap de la fila). */}
                    <button type="button" onClick={() => toggle(comp_0.id)} className="inline-flex min-h-7 shrink-0 whitespace-nowrap items-center gap-1 rounded-full bg-muted border border-border px-2.5 py-1 text-[11px] font-medium text-foreground-soft hover:bg-[var(--card)] hover:border-border-strong transition-colors" aria-expanded={isExpanded_0} aria-controls={`sub-${comp_0.id}`}>
                      {t("statusComponentsCount", {
                    count: String(def_0.componentsCount)
                  })}
                      <ChevronDown className={cn("size-3 transition-transform", isExpanded_0 && "rotate-180")} />
                    </button>
                  </div>

                  {/* Right: state only — uptime shown once in banner (no repetición) */}
                  <div className="flex items-center gap-2.5 shrink-0">
                    <span className="hidden sm:inline-flex items-center gap-1.5 text-[11px] text-muted-foreground whitespace-nowrap">
                      <span className={cn("size-2 rounded-full shrink-0", stateOk_0 ? "bg-[var(--success)]" : stateDeg_0 ? "bg-[var(--warning)]" : comp_0.state === "outage" ? "bg-[var(--danger)]" : "bg-muted-foreground")} />
                      {t(componentStateLabelKey(comp_0.state))}
                    </span>
                    {/* Mobile dot only */}
                    <span className={cn("sm:hidden size-2 rounded-full shrink-0", stateOk_0 ? "bg-[var(--success)]" : stateDeg_0 ? "bg-[var(--warning)]" : comp_0.state === "outage" ? "bg-[var(--danger)]" : "bg-muted-foreground")} aria-hidden="true" />
                  </div>
                </div>

                {/* Subcomponents — disclosure (2026-09-20 FASE 3 P1: --radius-panel,
                    no lovable-card anidada dentro de la sección-card). */}
                {isExpanded_0 && <div id={`sub-${comp_0.id}`} className="mt-3 rounded-[var(--radius-panel)] border border-border bg-muted p-3.5">
                    <ul className="space-y-2">
                      {def_0.sub.map(s_0 => <li key={s_0} className="flex items-center gap-2.5 text-xs text-foreground-soft">
                          <CheckCircle2 className={cn("size-3.5 shrink-0", stateOk_0 ? "text-[var(--success-text)]" : stateDeg_0 ? "text-[var(--warning)]" : "text-muted-foreground")} />
                          <span className="flex-1">{t(s_0)}</span>
                          <span className="ml-auto text-[11px] font-medium text-muted-foreground whitespace-nowrap">
                            {t(componentStateLabelKey(comp_0.state))}
                          </span>
                        </li>)}
                    </ul>
                  </div>}
              </div>;
        })}
        </div>
        {/* 2026-09-24 FASE 3 overflow ··· (patrón AdminTabNav MAX_VISIBLE=5):
            5 visibles + 1 trigger = 6 máx. Sin keys nuevas (navOpenMenu existe
            en 14 locales). Superficie opaca modal-surface, 1 sola capa. */}
        {overflowRows.length > 0 ? <details className="border-t border-border" data-testid="status-overflow">
            <summary aria-label={t("navOpenMenu")} className="flex min-h-11 cursor-pointer list-none items-center justify-center gap-1.5 px-4 py-2.5 text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] [&::-webkit-details-marker]:hidden">
              <span aria-hidden="true">···</span>
              <ChevronDown className="size-3.5" aria-hidden="true" />
              <span className="tabular-nums">+{overflowRows.length}</span>
            </summary>
            <div className="divide-y divide-border border-t border-border" role="list" data-testid="status-components-overflow">
              {overflowRows.map(comp_1 => renderStatusRow(comp_1))}
            </div>
          </details> : null}
      </section>

      <Dialog open={subscribeOpen} onOpenChange={open => {
      setSubscribeOpen(open);
      if (!open) setSubscribeError(null);
    }}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[15px]">
              <Mail className="size-4 text-muted-foreground" aria-hidden="true" />
              {t("statusSubscribeTitle")}
            </DialogTitle>
            <DialogDescription className="text-xs leading-relaxed">
              {/* 2026-09-21 FASE 4 transversal M4 (sin fallback inglés:
                  audit:i18n verde garantiza la key en 14 locales). */}
              {t("statusSubscribeDesc")}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={e_0 => void onSubscribe(e_0)} className="mt-2 space-y-3">
            <label htmlFor="status-subscribe-email" className="sr-only">
              {t("newsletterPlaceholder")}
            </label>
            {/* 2026-09-23 FASE 3 P2 radio fields (research 2026 form UX + F6 SSOT:
                inputs usan --radius-field 16px, no pill — el pill vive solo en
                el botón submit; 1 superficie, sin blur). */}
            <Input type="email" id="status-subscribe-email" required value={email} ref={subscribeInputRef} onChange={e_1 => {
            setEmail(e_1.target.value);
            if (subscribeError) setSubscribeError(null);
          }} placeholder={t("newsletterPlaceholder")} aria-invalid={subscribeError ? true : undefined} aria-describedby={subscribeError ? "status-subscribe-error" : undefined}
          // 2026-09-24 FASE 3 FIX design-auth-tokens (research 2026 WCAG
          // 2.2 2.5.8 + 3.3.1: h-10→h-11 target 44px + quita aria-label
          // duplicado — el <label htmlFor> sr-only ya nombra el campo).
          className="h-11 rounded-[var(--radius-field)] border-border bg-[var(--card)] px-4 text-sm" disabled={subLoading} autoComplete="email" data-testid="status-subscribe-email" />
            {subscribeError && <p id="status-subscribe-error" role="alert" className="text-xs font-medium text-[var(--danger)]">
                {subscribeError}
              </p>}
            <Button type="submit" disabled={subLoading} className="h-11 w-full rounded-full text-sm font-medium" data-testid="status-subscribe-submit">
              {subLoading ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : t("newsletterCta")}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
      {/* keep hidden latency for tests — single source, not duplicated per-component */}
      {typeof model.apiLatencyMs === "number" && <span className="sr-only" data-testid="status-latency-hidden">
          {t("statusApiLatency", {
        ms: String(model.apiLatencyMs)
      })}
        </span>}
    </div>;
}